import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';

import { useToast } from 'core/components/toast';
import { DropzoneContainer } from 'core/components/dropzone';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';

import useStyles from './document-upload.styles';

const DocumentUpload = ({ onUpload, loading }) => {
  const classes = useStyles();
  const [error, setError] = useState(null);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const toast = useToast();
  const { viewOnly } = useSession();
  const {
    offer: { documentsLoading: offerDocumentsLoading }
  } = useStore();
  const documentsLoading = typeof loading === 'boolean' ? loading : offerDocumentsLoading;

  const validateFile = useCallback((file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported. Please upload PDF, JPG, PNG, or DOC files';
    }

    return null;
  }, []);

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      if (viewOnly || documentsLoading || uploadingFileName) {
        return;
      }

      if (!acceptedFiles.length) {
        toast.notify({
          type: 'error',
          message: 'No file selected'
        });
        return;
      }

      const validFiles = [];
      for (const file of acceptedFiles) {
        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          toast.notify({
            type: 'error',
            message: `${file.name}: ${validationError}`
          });
          continue;
        }
        validFiles.push(file);
      }

      if (!validFiles.length) {
        return;
      }

      setError(null);
      setUploadingFileName(`uploading ${validFiles.length} file(s)`);

      try {
        await onUpload(validFiles.length === 1 ? validFiles[0] : validFiles);
      } catch {
        setError('Failed to upload document(s)');
      } finally {
        setUploadingFileName('');
      }
    },
    [viewOnly, documentsLoading, uploadingFileName, validateFile, onUpload, toast]
  );

  return (
    <div className={classes.container}>
      <DropzoneContainer
        onUpload={handleDrop}
        error={error}
        deleting={false}
        uploading={uploadingFileName}
        progress={0}
      />

      {!error && !documentsLoading && (
        <Grid container direction="column" alignItems="center" spacing={1} className={classes.supportText}>
          <Grid item>
            <Typography variant="body2" className={classes.supportedFormats}>
              Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
            </Typography>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

DocumentUpload.propTypes = {
  onUpload: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool
};

DocumentUpload.defaultProps = {
  disabled: false,
  loading: undefined
};

export default observer(DocumentUpload);
